import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ShieldCheck, Scale, AlertOctagon, CheckCircle2, Mail } from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import '../../styles/Home.css';
import '../../styles/Auth.css';

export default function TermsPage() {
  return (
    <div className="senritsu-container">
      <SEO
        title="Terms of Service | SenritsuX - Artist-First Music Ecosystem"
        description="Official Terms of Service for SenritsuX. Ensuring 100% master ownership retention for artists, clear distribution warranties, and transparent royalty payouts."
        canonical="/terms"
        keywords={['SenritsuX terms of service', 'artist distribution agreement', 'music master ownership']}
      />
      <Navbar />

      <main className="page-main-content">
        <section className="about-hero-section" style={{ paddingTop: '2.5rem', paddingBottom: '1.5rem' }}>
          <div className="about-hero-wrapper" style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <span className="page-badge">Legal Terms</span>
            <h1 className="about-hero-title" style={{ fontSize: '2.4rem', marginTop: '0.5rem' }}>
              Terms of <span className="title-highlight">Service</span>
            </h1>
            <p className="about-hero-subtitle" style={{ margin: '0 auto 1.5rem', maxWidth: '600px' }}>
              Effective Date: January 1, 2025 • Governs use of SenritsuX music distribution, beat marketplace, and artist services.
            </p>
          </div>
        </section>

        <div style={{ maxWidth: '850px', margin: '0 auto 4rem', padding: '0 1.5rem' }}>
          <div
            className="auth-card"
            style={{
              padding: '2.5rem',
              borderRadius: '16px',
              lineHeight: 1.8,
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.98rem',
            }}
          >
            {/* 1. Acceptance */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <FileText size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  1. Acceptance of Terms
                </h2>
              </div>
              <p>
                By registering an account, submitting music for distribution, or purchasing/selling beats on <strong style={{ color: '#fff' }}>https://senritsux.in</strong> (&quot;SenritsuX&quot;), you enter into a legally binding contract governed by these Terms of Service. If you do not agree to these terms, do not access or use SenritsuX services.
              </p>
            </div>

            {/* 2. 100% Artist Ownership Guarantee */}
            <div style={{ marginBottom: '2.5rem', background: 'rgba(217,251,6,0.06)', border: '1px solid rgba(217,251,6,0.25)', padding: '1.5rem', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <ShieldCheck size={24} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#d9fb06', margin: 0 }}>
                  2. 100% Artist Master Rights &amp; Ownership Guarantee
                </h2>
              </div>
              <p style={{ margin: 0, color: '#fff' }}>
                <strong>You retain 100% of your copyright, master sound recordings, compositions, trademarks, and visual assets.</strong> SenritsuX makes zero claim of ownership over your creations. You grant SenritsuX solely a non-exclusive, worldwide, sub-licensable license to ingest, encode, deliver, and collect royalties from Digital Service Providers (DSPs) solely for the duration you choose to distribute through our platform.
              </p>
            </div>

            {/* 3. Distribution Warranties & Content Standards */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <CheckCircle2 size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  3. Content Warranties &amp; Artist Responsibilities
                </h2>
              </div>
              <p>When you submit music, audio files, or artwork, you warrant and represent that:</p>
              <ul style={{ paddingLeft: '1.2rem' }}>
                <li>You are the sole author or hold documented, legal commercial licenses for all musical works, audio samples, beat leases, and cover art included in your release.</li>
                <li>Your submission does not infringe upon any third party&apos;s copyright, trademark, privacy, or moral rights.</li>
                <li>Your releases comply with DSP formatting standards (e.g., lossless 16-bit/44.1kHz or 24-bit WAV, 3000x3000px square cover art).</li>
              </ul>
            </div>

            {/* 4. Royalties & Payouts */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Scale size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  4. Royalties, Accounting &amp; Payments
                </h2>
              </div>
              <p>
                SenritsuX collects streaming and download revenue from partner DSPs and allocates your share (up to 97.5% depending on plan) to your user balance. Royalty statements are posted as received from DSPs (typically 45 to 90 days following the stream month). Withdrawals via UPI or Indian bank accounts are executed within scheduled monthly payout cycles.
              </p>
            </div>

            {/* 5. Beat Marketplace Rules */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <CheckCircle2 size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  5. Beat Marketplace Terms
                </h2>
              </div>
              <p>
                Producers uploading instrumentals warrant that all drum loops, melodies, and sound designs are either 100% original or legally cleared. Buyers are bound by the streaming and broadcast thresholds specified in the selected license tier (Non-Exclusive Lease vs Exclusive Buyout).
              </p>
            </div>

            {/* 6. Prohibited Activities */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <AlertOctagon size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  6. Artificial Streaming &amp; Prohibited Conduct
                </h2>
              </div>
              <p>
                SenritsuX maintains a zero-tolerance policy against artificial stream manipulation, bot farms, pay-for-stream networks, and automated looping schemes. Any account flagged by DSP anti-fraud algorithms faces immediate takedown, royalty withholding, and permanent ban.
              </p>
            </div>

            {/* 7. Governing Law */}
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Scale size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  7. Governing Law &amp; Jurisdiction
                </h2>
              </div>
              <p>
                These Terms are governed by and construed in accordance with the laws of the Republic of India. Any legal dispute or proceeding arising under these Terms shall be subject to the exclusive jurisdiction of the competent courts of India.
              </p>
            </div>

            {/* 8. Contact */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <Mail size={22} color="#d9fb06" />
                <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                  8. Inquiries &amp; Legal Notices
                </h2>
              </div>
              <p>
                For questions regarding these Terms or legal notices, please write to:
              </p>
              <div style={{ background: 'rgba(255,255,255,0.06)', padding: '1rem 1.2rem', borderRadius: '10px' }}>
                <p style={{ margin: '0 0 0.4rem' }}><strong>Email:</strong> <a href="mailto:support@senritsux.in" style={{ color: '#d9fb06' }}>support@senritsux.in</a></p>
                <p style={{ margin: 0 }}><strong>Organization:</strong> SenritsuX Music Ecosystem</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
